/**
 * tagged.js - the people tagged on a task, as pure helpers (Sharif, 10/6/26:
 * "tag multiple people on the tasks").
 *
 * A task keeps ONE owner, the person responsible. `tagged` is everyone else
 * involved: display names from the user picker, stored the way the owner is.
 * The server tidies the list the same way (server/lib/crm.js tidyTagged):
 * trimmed, one entry per person ignoring case, never the owner, at most 20.
 * "Mine" means a task I own OR am tagged on.
 *
 * Kept free of React so __tests__/taggedPeople.test.jsx can check them directly.
 */

export const TAGGED_MAX = 20;

const key = (v) =>
  String(v == null ? '' : v)
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/** A task's tagged names as a list ([] for an older row, or a KSA one, with none). */
export const taggedOf = (t) => (t && Array.isArray(t.tagged) ? t.tagged.filter((n) => typeof n === 'string' && n.trim()) : []);

/** The list tidied against `owner`, the server's rule (never the owner, once each, no blanks). */
export function tidyTagged(list, owner) {
  const seen = new Set(['', key(owner)]);
  const out = [];
  for (const v of Array.isArray(list) ? list : []) {
    if (typeof v !== 'string') continue;
    const name = v.trim().replace(/\s+/g, ' ');
    if (seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push(name);
  }
  return out;
}

/**
 * Is `name` (a task's owner or a tagged name) the signed-in user? By display
 * name or email, any case; a name stored as an email also matches through
 * the users list.
 */
export function isMe(name, user, users) {
  if (!user) return false;
  const me = [user.name, user.email].map(key).filter(Boolean);
  const k = key(name);
  if (!k) return false;
  if (me.includes(k)) return true;
  if (!k.includes('@')) return false;
  const hit = (users || []).find((u) => u && key(u.email) === k);
  return !!(hit && me.includes(key(hit.name)));
}

/** Am I on this task's tagged list? */
export const isTaggedMe = (t, user, users) => taggedOf(t).some((n) => isMe(n, user, users));

/** I am on it only because I was tagged (not its owner): the rows show a "Tagged" chip. */
export const taggedNotOwner = (t, user, users) => !!t && !isMe(t.owner, user, users) && isTaggedMe(t, user, users);

/** "Paul Sarantos +2": the first name and how many more; '' for nobody. */
export function taggedShort(names) {
  const list = Array.isArray(names) ? names : [];
  if (!list.length) return '';
  return list.length === 1 ? list[0] : `${list[0]} +${list.length - 1}`;
}

/** The tooltip / screen-reader text: "Tagged: Paul Sarantos, Shawn Rimmell". */
export const taggedTitle = (names) => (Array.isArray(names) && names.length ? `Tagged: ${names.join(', ')}` : '');

/**
 * The people a "Tag people" picker offers: every name in `people` that is not
 * the owner, not already tagged and (when `query` is typed) contains it.
 */
export function tagSuggestions(people, { owner, tagged, query = '', limit = 8 } = {}) {
  const taken = new Set([key(owner), ...(tagged || []).map(key)]);
  const q = key(query);
  const out = [];
  for (const n of people || []) {
    if (!n || taken.has(key(n))) continue;
    if (q && !key(n).includes(q)) continue;
    out.push(n);
    if (out.length >= limit) break;
  }
  return out;
}
